/**
 * "Your keys": one place for what you hold and what you can claim.
 * - keys held and your share of all keys
 * - claimable test FRAME from key splits (withdrawDividends)
 * - claimable faction pots across every claimable round (claimFactionJackpot(roundId))
 * - one "Claim all" button that sends the claims one after another with progress
 * Also hosts the first-run checklist (get test FRAME → allow the game to use it → buy keys).
 * Test FRAME is a testnet token with no value. $FRAME has not launched.
 */
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { formatUnits, parseAbi, type Address } from 'viem';
import { useAccount, useReadContracts, useWriteContract, useConfig } from 'wagmi';
import { waitForTransactionReceipt } from 'wagmi/actions';
import { CHAIN } from '../config/chain';
import { ACTIVE_CHAIN } from '../wallet/config';
import { useFaucet } from './FaucetButton';
import SeasonBadge from './SeasonBadge';

const GAME = CHAIN.gameAddress as Address;
const TOKEN = CHAIN.frameTokenAddress as Address;
/** Faction pot payouts went live on-chain in round 1210. */
const FACTION_POT_FIRST_ROUND = 1210;
const MAX_ROUNDS_SCANNED = 300;
export const BASE_SEPOLIA_GAS_FAUCET = 'https://docs.base.org/base-chain/tools/network-faucets';

const GAME_ABI = parseAbi([
  'function keyBalance(address) view returns (uint256)',
  'function totalKeys() view returns (uint256)',
  'function withdrawableDividendsOf(address) view returns (uint256)',
  'function factionJackpotClaimable(uint256 roundId, address player) view returns (uint256)',
  'function withdrawDividends()',
  'function claimFactionJackpot(uint256 roundId)',
]);
const TOKEN_ABI = parseAbi([
  'function balanceOf(address) view returns (uint256)',
  'function allowance(address owner, address spender) view returns (uint256)',
]);

const fmt = (v: bigint | undefined) => {
  const n = Number(formatUnits(v ?? 0n, 18));
  return n >= 1000 ? n.toLocaleString(undefined, { maximumFractionDigits: 0 }) : n.toLocaleString(undefined, { maximumFractionDigits: n < 1 ? 4 : 2 });
};

type Step = { label: string; state: 'pending' | 'sending' | 'done' | 'failed' };

export default function YourKeysPanel({ currentRoundId, refreshTrigger = 0, onClaimed, onGoToBuy }: {
  currentRoundId: number; refreshTrigger?: number; onClaimed?: () => void; onGoToBuy?: () => void;
}) {
  const { address, chainId } = useAccount();
  const wagmiConfig = useConfig();
  const { writeContractAsync } = useWriteContract();
  const faucet = useFaucet(address ?? null);
  const onRightChain = !!address && chainId === ACTIVE_CHAIN.id;
  const [steps, setSteps] = useState<Step[] | null>(null);
  const [checklistHidden, setChecklistHidden] = useState(() => { try { return localStorage.getItem('nt3d_checklist_done') === '1'; } catch { return false; } });

  const q = { enabled: !!address, refetchInterval: 30000 } as const;
  const base = useReadContracts({
    allowFailure: true,
    contracts: address ? [
      { address: GAME, abi: GAME_ABI, functionName: 'keyBalance', args: [address], chainId: ACTIVE_CHAIN.id },
      { address: GAME, abi: GAME_ABI, functionName: 'totalKeys', chainId: ACTIVE_CHAIN.id },
      { address: GAME, abi: GAME_ABI, functionName: 'withdrawableDividendsOf', args: [address], chainId: ACTIVE_CHAIN.id },
      { address: TOKEN, abi: TOKEN_ABI, functionName: 'balanceOf', args: [address], chainId: ACTIVE_CHAIN.id },
      { address: TOKEN, abi: TOKEN_ABI, functionName: 'allowance', args: [address, GAME], chainId: ACTIVE_CHAIN.id },
    ] : [],
    query: { ...q, meta: { refreshTrigger } },
  });
  const r = base.data ?? [];
  const keys = r[0]?.result as bigint | undefined;
  const totalKeys = r[1]?.result as bigint | undefined;
  const keySplit = r[2]?.result as bigint | undefined;
  const frameBal = r[3]?.result as bigint | undefined;
  const allowance = r[4]?.result as bigint | undefined;

  // Faction pots: every finished round since payouts went live (capped so the scan stays light).
  const rounds = useMemo(() => {
    const last = currentRoundId - 1;
    const first = Math.max(FACTION_POT_FIRST_ROUND, last - MAX_ROUNDS_SCANNED + 1);
    const out: number[] = [];
    for (let i = last; i >= first; i--) out.push(i);
    return out;
  }, [currentRoundId]);
  const pots = useReadContracts({
    allowFailure: true,
    contracts: address ? rounds.map(id => ({ address: GAME, abi: GAME_ABI, functionName: 'factionJackpotClaimable' as const, args: [BigInt(id), address] as const, chainId: ACTIVE_CHAIN.id })) : [],
    query: { enabled: !!address && rounds.length > 0, refetchInterval: 60000 },
  });
  const claimablePots = useMemo(() => (pots.data ?? []).map((x, i) => ({ round: rounds[i], amount: (x.result as bigint | undefined) ?? 0n })).filter(p => p.amount > 0n), [pots.data, rounds]);
  const potsTotal = claimablePots.reduce((s, p) => s + p.amount, 0n);
  const nothingToClaim = (keySplit ?? 0n) === 0n && potsTotal === 0n;

  const sharePct = keys && totalKeys && totalKeys > 0n ? Number((keys * 1_000_000n) / totalKeys) / 10_000 : 0;
  const busy = !!steps && steps.some(s => s.state === 'sending' || s.state === 'pending');

  const refetchAll = useCallback(() => { void base.refetch(); void pots.refetch(); }, [base, pots]);

  const claimAll = useCallback(async () => {
    if (!address || busy || nothingToClaim) return;
    const plan: { label: string; run: () => Promise<`0x${string}`> }[] = [];
    if ((keySplit ?? 0n) > 0n) plan.push({ label: `Key share: ${fmt(keySplit)} test FRAME`, run: () => writeContractAsync({ address: GAME, abi: GAME_ABI, functionName: 'withdrawDividends', chainId: ACTIVE_CHAIN.id }) });
    for (const p of claimablePots) plan.push({ label: `Faction pot, round #${p.round}: ${fmt(p.amount)} test FRAME`, run: () => writeContractAsync({ address: GAME, abi: GAME_ABI, functionName: 'claimFactionJackpot', args: [BigInt(p.round)], chainId: ACTIVE_CHAIN.id }) });
    const st: Step[] = plan.map(p => ({ label: p.label, state: 'pending' }));
    setSteps([...st]);
    let ok = 0;
    for (let i = 0; i < plan.length; i++) {
      st[i] = { ...st[i], state: 'sending' }; setSteps([...st]);
      try {
        const hash = await plan[i].run();
        await waitForTransactionReceipt(wagmiConfig, { hash, chainId: ACTIVE_CHAIN.id });
        st[i] = { ...st[i], state: 'done' }; ok++;
      } catch (e: any) {
        st[i] = { ...st[i], state: 'failed' };
        if (/reject|denied|cancel/i.test(String(e?.shortMessage || e?.message))) {
          for (let j = i + 1; j < plan.length; j++) st[j] = { ...st[j], state: 'failed' };
          setSteps([...st]);
          toast('Claim stopped. You can pick up the rest any time.');
          break;
        }
      }
      setSteps([...st]);
    }
    if (ok > 0) toast.success(`Collected ${ok} of ${plan.length} claim${plan.length > 1 ? 's' : ''} (test FRAME, testnet only).`);
    refetchAll();
    onClaimed?.();
  }, [address, busy, nothingToClaim, keySplit, claimablePots, writeContractAsync, wagmiConfig, refetchAll, onClaimed]);

  // ---- checklist state
  const hasFrame = (frameBal ?? 0n) > 0n;
  const hasAllowance = (allowance ?? 0n) > 0n;
  const hasKeys = (keys ?? 0n) > 0n;
  const showChecklist = !checklistHidden && !(address && hasKeys);
  const openWallet = () => window.dispatchEvent(new Event('nt3d:open-wallet'));

  const checklist = showChecklist && (
    <div className="yk-checklist" aria-label="Getting started">
      <div className="yk-cl-head"><b>Get started</b><span className="mono">testnet · test FRAME has no value</span>
        <button type="button" className="yk-link" onClick={() => { setChecklistHidden(true); try { localStorage.setItem('nt3d_checklist_done', '1'); } catch { /* ignore */ } }}>Hide</button>
      </div>
      <ol>
        <li className={address && hasFrame ? 'done' : ''}>
          <span className="yk-tick" aria-hidden="true">{address && hasFrame ? '✓' : '1'}</span>
          <div>
            <b>Get test FRAME</b>
            <p>Free from our faucet, once per wallet every 24h. You also need a little Base Sepolia ETH for gas: <a href={BASE_SEPOLIA_GAS_FAUCET} target="_blank" rel="noreferrer">get free test ETH</a>.</p>
            {!address ? <button type="button" className="holo-btn sm" onClick={openWallet}>🚰 Connect to get test FRAME</button>
              : !onRightChain ? <span className="mono yk-warn">Switch to {CHAIN.chainName} first (see the banner above).</span>
              : hasFrame ? <span className="mono">You have {fmt(frameBal)} test FRAME.</span>
              : <button type="button" className="holo-btn sm" disabled={faucet.busy || faucet.check.status === 'cooldown'} onClick={() => void faucet.request()}>🚰 {faucet.busy ? 'Sending…' : faucet.check.status === 'cooldown' ? `Faucet in ${faucet.check.hoursLeft ?? '?'}h` : 'Get test FRAME'}</button>}
          </div>
        </li>
        <li className={address && hasAllowance ? 'done' : ''}>
          <span className="yk-tick" aria-hidden="true">{address && hasAllowance ? '✓' : '2'}</span>
          <div>
            <b>Allow the game to use your test FRAME</b>
            <p>Your wallet asks once so the game contract can take test FRAME when you buy keys. By default you allow only the exact amount of your buy. You can tick "allow unlimited" in the buy panel to skip this step next time; you can revoke it in your wallet any time.</p>
            {address && !hasAllowance && <button type="button" className="holo-btn ghost sm" onClick={onGoToBuy}>Go to the buy panel</button>}
          </div>
        </li>
        <li className={address && hasKeys ? 'done' : ''}>
          <span className="yk-tick" aria-hidden="true">{address && hasKeys ? '✓' : '3'}</span>
          <div>
            <b>Buy keys</b>
            <p>Each key adds 30s to the clock and gives you a share of later key buys, paid in test FRAME. The last buyer when the clock hits zero wins the pot.</p>
            {address && <button type="button" className="holo-btn sm" onClick={onGoToBuy}>Buy keys</button>}
          </div>
        </li>
      </ol>
    </div>
  );

  if (!address) {
    return (
      <div className="yk">
        <p className="yk-empty">Connect a wallet to see your keys and anything you can claim.</p>
        {checklist}
      </div>
    );
  }

  return (
    <div className="yk">
      {checklist}
      {address && <div className="yk-title" style={{ marginBottom: 6 }}><SeasonBadge address={address} /></div>}
      <div className="yk-stats">
        <div className="holo-stat"><div className="k">Keys held</div><div className="v">{keys === undefined ? '…' : Number(keys).toLocaleString()}</div></div>
        <div className="holo-stat"><div className="k">Share of all keys</div><div className="v">{keys === undefined ? '…' : `${sharePct < 0.01 && sharePct > 0 ? '<0.01' : sharePct.toFixed(2)}%`}</div></div>
        <div className="holo-stat" title="Every key you hold gets a cut of later key buys, in test FRAME. No locking: hold keys, then claim whenever you want.">
          <div className="k">Key share to claim</div><div className="v">{fmt(keySplit)} <small>test FRAME</small></div></div>
        <div className="holo-stat" title="Your part of the faction pot from finished rounds, split by the keys you bought for your faction.">
          <div className="k">Faction pots to claim</div><div className="v">{pots.isLoading ? '…' : fmt(potsTotal)} <small>test FRAME{claimablePots.length > 0 ? ` · ${claimablePots.length} round${claimablePots.length > 1 ? 's' : ''}` : ''}</small></div></div>
      </div>

      <div className="yk-actions">
        <button type="button" className="holo-btn pri" disabled={busy || nothingToClaim || !onRightChain} onClick={() => void claimAll()}>
          {busy ? 'Claiming…' : 'Claim all'}
        </button>
        <span className="mono yk-reason">
          {!onRightChain ? `Switch to ${CHAIN.chainName} to claim.`
            : !hasKeys && nothingToClaim ? 'You hold 0 keys. Buy a key to get a share of later buys.'
            : nothingToClaim ? 'Nothing new since your last claim. Your share grows as other players buy keys.'
            : `${(keySplit ?? 0n) > 0n ? 1 : 0} key share + ${claimablePots.length} faction pot claim${claimablePots.length === 1 ? '' : 's'}, sent one after another.`}
        </span>
      </div>

      {steps && (
        <ol className="yk-steps" aria-live="polite">
          {steps.map((s, i) => (
            <li key={i} className={s.state}><span aria-hidden="true">{s.state === 'done' ? '✓' : s.state === 'failed' ? '✕' : s.state === 'sending' ? '…' : '○'}</span> {s.label}{s.state === 'sending' ? ' · confirm in your wallet' : ''}</li>
          ))}
        </ol>
      )}
    </div>
  );
}
